import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import isNil from 'lodash/isNil';
import { Observable } from 'relay-runtime';
import { fg } from '@atlaskit/platform-feature-flags';
import { putExtensions } from '@atlassian/relay-capture-extensions';
import { getAggEndpoint, getRequestBody } from '@atlassian/relay-endpoint';
import { CompletedQueryPromisesMap, QueryPromisesMap } from '@atlassian/relay-query-promises';
import { addTraceId } from '@atlassian/relay-traceid';
import { getClientDispatcher } from '@atlassian/server-to-client-comms/client-dispatcher';
import { registerEvent } from '@atlassian/server-to-client-comms/server-receiver';
import { buildGraphqlSmartFetch, processMultipartPromise } from './multipart-fetch';
import { getRetryAttemptsForQuery, retryOnError, shouldRetry } from './utils/retries';
import { getTraceIdFromExtensions } from './utils/trace';
var isSsrHandoverIncrementsEnabled = function isSsrHandoverIncrementsEnabled() {
  return Boolean(globalThis.__SSR_HANDOVER_INCREMENTS);
};
var moduleID = 'relay-fetch';
export var fetch = function fetch(request, variables, cacheConfig, dependencies) {
  var _dependencies$getLogg;
  var queryName = request.name,
    operationKind = request.operationKind;
  var loggingMetadata = {
    queryName: queryName,
    operationKind: operationKind
  };
  var loggingAndMetrics = dependencies === null || dependencies === void 0 || (_dependencies$getLogg = dependencies.getLoggingAndMetricsHelper) === null || _dependencies$getLogg === void 0 ? void 0 : _dependencies$getLogg.call(dependencies, loggingMetadata);
  function augmentResponse(response) {
    addTraceId(queryName, getTraceIdFromExtensions(response));
    putExtensions(queryName, response.extensions);
  }

  // TODO: This needs to be changed by removing each Product, and only if all keys are empty final output is null
  function enforceNonNullability(response) {
    if (response.data && (response.data.jira === null || response.data.customerService === null || response.data.graphStore === null)) {
      // Enforce non-nullability of the jira and customerService namespaces. This is an intermediary solution to
      // prevent server errors/gateway timeouts from wiping the Relay store. Eventually we'd like this
      // to be enforced at a schema level, see further discussion https://hello.atlassian.net/wiki/spaces/~236031707/pages/2034382845/Top+level+errors+for+namespaces+in+AGG+Relay
      // @ts-expect-error - TS2322 - Relay types do not allow null data for a response with errors, however this is valid according to the spec http://spec.graphql.org/June2018/#sec-Data
      response.data = null;
    }
  }
  return Observable.create(function (sink) {
    try {
      var requestID = 'cacheID' in request && request.cacheID != null ? request.cacheID : request.id;
      // Unique identifier for the server-to-client channel.
      var sinkID = "".concat(moduleID, "-").concat(requestID);
      var resolveCompletedResponse;
      var completedResponse = new Promise(function (resolve) {
        resolveCompletedResponse = resolve;
      });
      var finalise = function finalise() {
        resolveCompletedResponse();
        sink.complete();
      };
      var handleResponse = function handleResponse(response) {
        var payload = null;
        if ('incremental' in response && response.incremental) {
          if (response.incremental.length) {
            // Relay is expecting the contents of this array rather than the entire array itself. Also, Relay doesn't really care about tracking
            // the pending versus completed values, so we can just return the array without the extra account keeping.
            // @ts-expect-error It's hard to type this correctly because the payload sometimes has errors, sometimes not. Relay accounts for it,
            // TypeScript can't handle the polymorphism.
            payload = response.incremental;
          } else if (response.hasNext === false) {
            // It's possible for the server to send back an empty final increment.
            // Attempt to make Relay happy.
            payload = {
              data: null,
              extensions: {
                is_final: true
              }
            };
          }
        } else {
          // @ts-expect-error Typescript isn't happy that sometimes errors is undefined, but relay will manage just fine.
          payload = response;
        }

        // Notify observers that we have more data!
        if (payload) {
          sink.next(payload);
        }

        // Close the stream if there are no more responses.
        if ('hasNext' in response && response.hasNext === false) {
          finalise();
        }
      };

      // handles getting multipart response
      var getData = function getData() {
        var _dependencies$earlySc;
        var endpoint = getAggEndpoint(request, cacheConfig);
        var body = getRequestBody(request, variables);
        var options = {
          body: body
        };

        // Apply setOptions if provided
        if (dependencies !== null && dependencies !== void 0 && dependencies.setOptions && !fg('createrelayenvironment_-_setoptions_to_getheaders')) {
          options = dependencies.setOptions({
            endpoint: endpoint,
            options: options,
            request: request,
            variables: variables
          });
        }

        // Apply getHeaders if provided to update headers
        if (dependencies !== null && dependencies !== void 0 && dependencies.getHeaders && fg('createrelayenvironment_-_setoptions_to_getheaders')) {
          var headers = dependencies.getHeaders({
            endpoint: endpoint,
            options: options,
            request: request,
            variables: variables
          });
          options = _objectSpread(_objectSpread({}, options), {}, {
            headers: _objectSpread(_objectSpread({}, options.headers), headers)
          });
        }
        var fetchOverNetwork = buildGraphqlSmartFetch({
          url: dependencies !== null && dependencies !== void 0 && dependencies.setEndpoint ? dependencies === null || dependencies === void 0 ? void 0 : dependencies.setEndpoint(endpoint) : endpoint,
          options: options,
          variables: variables,
          request: request,
          isServer: (dependencies === null || dependencies === void 0 ? void 0 : dependencies.isServer) || false,
          cacheConfig: cacheConfig
        });
        var earlyScriptPromise = dependencies === null || dependencies === void 0 || (_dependencies$earlySc = dependencies.earlyScriptPromise) === null || _dependencies$earlySc === void 0 ? void 0 : _dependencies$earlySc.call(dependencies, request, variables);
        var fetchFn = fetchOverNetwork;
        var initialSource = fetchFn;
        if (dependencies !== null && dependencies !== void 0 && dependencies.withErrorHandling) {
          var withErrorHandling = dependencies.withErrorHandling;
          initialSource = function initialSource() {
            return !(dependencies !== null && dependencies !== void 0 && dependencies.isServer) && earlyScriptPromise ? withErrorHandling(earlyScriptPromise) : withErrorHandling(fetchFn());
          };
        }
        var responses = processMultipartPromise(function () {
          return retryOnError(initialSource, {
            retryFunc: fetchFn,
            retryPredicate: shouldRetry,
            retryAttempts: getRetryAttemptsForQuery(request),
            onRetry: loggingAndMetrics === null || loggingAndMetrics === void 0 ? void 0 : loggingAndMetrics.logErrorRetry
          });
        });

        // Recreate a bizarre behaviour that will cause the observable to hang indefinitely when the user is not authenticated, presumably because we redirect the user to log in...
        // This should be revised; see https://jplat.atlassian.net/browse/MAGMA-3087
        var responseShouldHang = false;
        var firstResponseUsed = false;
        var resolveFirstResponse;
        var firstResponse = new Promise(function (resolve) {
          resolveFirstResponse = resolve;
        });
        responses.subscribe({
          start: function start() {
            if (requestID != null) {
              if (dependencies !== null && dependencies !== void 0 && dependencies.isServer) {
                // so that SSR SPA knows it is safe to render
                var renderAfterThisResponse = isSsrHandoverIncrementsEnabled() ? firstResponse : completedResponse;
                QueryPromisesMap.set(requestID, renderAfterThisResponse);
              } else {
                QueryPromisesMap.set(requestID, firstResponse);
              }
              CompletedQueryPromisesMap.set(requestID, completedResponse);
            }
          },
          next: function next(response) {
            if (dependencies !== null && dependencies !== void 0 && dependencies.isServer && isSsrHandoverIncrementsEnabled() && firstResponseUsed) {
              // Send the response payload to a side-channel.
              // TODO MAGMA-3204: improve robustness. Should be sending ID of module to communicate with, sinkID should be an argument.
              registerEvent({
                id: sinkID,
                name: 'default',
                args: [response]
              });
              if ('hasNext' in response && response.hasNext === false) {
                finalise();
              }

              // Don't send any further responses to the fetch subscriber, since we'll be sending them through a side-channel.
              return;
            }
            if (!firstResponseUsed) {
              firstResponseUsed = true;
              // according to spec, we can assume the first response is a GraphQLSingularResponse.
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              var singularResponse = response;
              augmentResponse(singularResponse);
              if (dependencies !== null && dependencies !== void 0 && dependencies.validateResponse && !dependencies.validateResponse(request, singularResponse)) {
                var _dependencies$onInval;
                dependencies === null || dependencies === void 0 || (_dependencies$onInval = dependencies.onInvalidResponse) === null || _dependencies$onInval === void 0 || _dependencies$onInval.call(dependencies, request, singularResponse);
                responseShouldHang = true;
                return;
              }
              if (fg('platform_relay_use_new_non_nullability')) {
                response = enforceProductNonNullability(singularResponse);
              } else {
                enforceNonNullability(singularResponse);
              }

              // write cache query response on SSR
              // only write cache query response once or else this will break the assumption of graphql response being singular response
              if (dependencies !== null && dependencies !== void 0 && dependencies.isServer && requestID != null) {
                var _dependencies$queryRe;
                dependencies === null || dependencies === void 0 || (_dependencies$queryRe = dependencies.queryResponseCache) === null || _dependencies$queryRe === void 0 || _dependencies$queryRe.appendWithMetadata(requestID, variables, singularResponse, loggingMetadata);
              }
            }
            handleResponse(response);

            // signal that the initial response has been received
            resolveFirstResponse();

            // After the initial response, further responses are sent through a side-channel.
            if (dependencies !== null && dependencies !== void 0 && dependencies.isServer && isSsrHandoverIncrementsEnabled()) {
              sink.complete();
            }
          },
          error: function error(_error) {
            if (!firstResponseUsed) {
              firstResponseUsed = true;
              // todo MAGMA-3172: check control flows for fetch failure cases on server + client. Seems weird that
              // the previous construction of the function would not send a signal that the response was received
              // in the error case...
              // resolveFirstResponse();
            }
            loggingAndMetrics === null || loggingAndMetrics === void 0 || loggingAndMetrics.logError(_error);
            sink.error(_error);
          },
          complete: function complete() {
            if (responseShouldHang) {
              return;
            }
            finalise();
          }
        });
      };

      // On client...
      if (!(dependencies !== null && dependencies !== void 0 && dependencies.isServer) && requestID != null) {
        var _dependencies$queryRe2;
        // read cache from window.SPA_STATE if available
        var ssrCache = dependencies === null || dependencies === void 0 || (_dependencies$queryRe2 = dependencies.queryResponseCache) === null || _dependencies$queryRe2 === void 0 ? void 0 : _dependencies$queryRe2.get(requestID, variables);
        if (!isNil(ssrCache)) {
          var _dependencies$queryRe3;
          dependencies === null || dependencies === void 0 || (_dependencies$queryRe3 = dependencies.queryResponseCache) === null || _dependencies$queryRe3 === void 0 || _dependencies$queryRe3.delete(requestID, variables);
          sink.next(ssrCache);
          if (isSsrHandoverIncrementsEnabled() && 'hasNext' in ssrCache && ssrCache.hasNext) {
            // TODO MAGMA-3017: what about SSR fallback mode, or scenarios where SSR has partially failed?
            // TODO MAGMA-3204: improve robustness. Should be using a middle-man module rather than client dispatcher directly.
            // Receive response events as they are streamed from the server.
            getClientDispatcher().register(sinkID, handleResponse);
            getClientDispatcher().onAbort(sinkID, function (_ref) {
              var reason = _ref.reason;
              if (!sink.closed) {
                var _dependencies$handleA;
                dependencies === null || dependencies === void 0 || (_dependencies$handleA = dependencies.handleAbort) === null || _dependencies$handleA === void 0 || _dependencies$handleA.call(dependencies, request, reason);
                // if server throws an exception (such as SSR hanging up due to deadline exceeded)
                // refetch multipart data again via network
                getData();
              }
            });
          } else {
            finalise();
          }
          return;
        }
      }
      getData();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error) {
      loggingAndMetrics === null || loggingAndMetrics === void 0 || loggingAndMetrics.logError(error);
      sink.error(error);
    }
  });
};
export function enforceProductNonNullability(response) {
  if (response.data) {
    // Enforce non-nullability of the product namespaces. This is an intermediary solution to
    // prevent server errors/gateway timeouts from wiping the Relay store. Eventually we'd like this
    // to be enforced at a schema level, see further discussion https://hello.atlassian.net/wiki/spaces/~236031707/pages/2034382845/Top+level+errors+for+namespaces+in+AGG+Relay
    var sanitisedData = Object.assign({}, response.data);
    var productKeys = ['jira', 'customerService', 'graphStore'];
    for (var _i = 0, _productKeys = productKeys; _i < _productKeys.length; _i++) {
      var product = _productKeys[_i];
      if (sanitisedData[product] === null) {
        delete sanitisedData[product];
      }
    }
    if (Object.keys(sanitisedData).length === 0) {
      // @ts-expect-error - TS2322 - Relay types do not allow null data for a response with errors, however this is valid according to the spec http://spec.graphql.org/June2018/#sec-Dataa
      sanitisedData = null;
    }
    response.data = sanitisedData;
  }
  return response;
}